/**
 * Alerts API Module
 *
 * Frontend API client for alert management endpoints.
 *
 * Requirements:
 * - FR-035: View list of alerts
 * - FR-036: Acknowledge and resolve alerts
 * - FR-037: Alert details and history
 */

import apiClient from '../services/api';

// Types - matches camelCase from backend API
export interface Alert {
  id: string;
  lakehouseId: string;
  lakehouseName?: string;
  workspaceName?: string;
  /** Migration 165: representative dbo.alert_junction connection (what triggered the alert) */
  entityType?: string | null;
  /** Spec 193: source entity GUID + engine resource_type, for deep-linking to the source page */
  entityId?: string | null;
  resourceType?: string | null;
  entityName?: string | null;
  /** Spec 265 FR-025b: the collection stage ("<stage>" or "<stage>/<unit>") for deep-linking. */
  sourceScope?: string | null;
  connectionCount?: number;
  alertType: string;
  type?: string; // alias for alertType in some responses
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'active' | 'acknowledged' | 'resolved';
  message: string;
  title?: string;
  escalationLevel?: number;
  acknowledgedBy?: string | null;
  acknowledgedByName?: string | null;   // Spec 255: resolved display name (list + detail)
  acknowledgedAt?: string | null;
  resolvedBy?: string | null;
  resolvedByName?: string | null;       // Spec 255: resolved display name (list + detail)
  resolvedAt?: string | null;
  resolution?: string;
  snoozedUntil?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AlertFilters {
  status?: string;
  severity?: string;
  alertType?: string;
  lakehouseId?: string;
  startDate?: string;
  endDate?: string;
  /** Spec 255: filter to alerts acknowledged by this user id (the owner). */
  acknowledgedBy?: string;
  /** Spec 255: filter to alerts resolved by this user id (the clearer). */
  resolvedBy?: string;
}

/** Spec 255: multi-user actor analytics for the Alerts page. */
export interface ActorCount {
  userId: string;
  name: string;
  count: number;
}
export interface SeverityHistoryPoint {
  date: string;
  critical: number;
  warning: number;
  info: number;
}
export interface AlertAnalytics {
  owners: ActorCount[];        // open + acknowledged, by owner (the Owned tile breakdown)
  resolvers: ActorCount[];     // resolved, by clearer (the Resolved tile breakdown)
  acknowledgers: ActorCount[]; // distinct acknowledgers for the "Acknowledged by" filter
  severityHistory: SeverityHistoryPoint[];
}

export interface AlertSummary {
  total: number;
  /** All unresolved (= unacknowledged + acknowledged); kept for back-compat. */
  active: number;
  /** Unresolved AND not acknowledged — the canonical "needs attention" count (bell badge). */
  unacknowledged: number;
  /** Unresolved AND acknowledged. */
  acknowledged: number;
  /** Resolved (all-time). unacknowledged + acknowledged + resolved = total. */
  resolved: number;
  /** All-time counts keyed by lowercased severity (e.g. { critical: 46, warning: 3 }). */
  bySeverity?: Record<string, number>;
  // Spec 255 multi-user KPIs
  /** Alerts CLEARED in the last 24h (who's clearing them). */
  resolvedLast24h?: number;
  /** ISO timestamp of the OLDEST open alert with no owner (null = none unattended). */
  oldestUnattendedAt?: string | null;
  /** Distinct people currently owning (acknowledged, unresolved) an open alert. */
  activeOwners?: number;
}

export interface AlertHistoryEntry {
  action: string;
  timestamp: string;
  details: Record<string, unknown>;
}

export interface AlertsResponse {
  items: Alert[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface NotificationConfig {
  email: {
    enabled: boolean;
    recipients: string[];
  };
  teams: {
    enabled: boolean;
  };
}

export interface NotificationTestResult {
  email: { available: boolean; success: boolean; error?: string };
  teams: { available: boolean; success: boolean; error?: string };
}

class AlertsApi {
  private readonly basePath = '/api/v1/alerts';

  /**
   * Get list of alerts with optional filtering and pagination
   */
  async getAlerts(
    params: AlertFilters & {
      isAcknowledged?: boolean;
      isResolved?: boolean;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<AlertsResponse> {
    const queryParams = new URLSearchParams();

    if (params.status) {
      queryParams.append('status', params.status);
    }
    // Status filter drives these two flags (active/acknowledged/resolved). Previously
    // dropped here → the Status dropdown had no effect on the list.
    if (params.isAcknowledged !== undefined) {
      queryParams.append('isAcknowledged', String(params.isAcknowledged));
    }
    if (params.isResolved !== undefined) {
      queryParams.append('isResolved', String(params.isResolved));
    }
    if (params.severity) {
      queryParams.append('severity', params.severity);
    }
    if (params.alertType) {
      queryParams.append('alertType', params.alertType);
    }
    if (params.lakehouseId) {
      queryParams.append('lakehouseId', params.lakehouseId);
    }
    if (params.acknowledgedBy) {
      queryParams.append('acknowledgedBy', params.acknowledgedBy);
    }
    if (params.resolvedBy) {
      queryParams.append('resolvedBy', params.resolvedBy);
    }
    if (params.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    if (params.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }

    const response = await apiClient.get<AlertsResponse>(
      `${this.basePath}?${queryParams.toString()}`
    );
    return response;
  }

  /**
   * Get alert summary statistics
   */
  async getSummary(): Promise<AlertSummary> {
    const response = await apiClient.get<AlertSummary>(`${this.basePath}/summary`);
    return response;
  }

  /**
   * Get multi-user actor analytics (owner/resolver breakdowns + severity history).
   */
  async getAnalytics(historyDays = 14): Promise<AlertAnalytics> {
    const response = await apiClient.get<AlertAnalytics>(
      `${this.basePath}/analytics?historyDays=${historyDays}`
    );
    return response;
  }

  /**
   * Get alert details by ID
   */
  async getById(id: string): Promise<Alert> {
    const response = await apiClient.get<Alert>(`${this.basePath}/${id}`);
    return response;
  }

  /**
   * Acknowledge an alert
   */
  async acknowledge(id: string, notes?: string): Promise<Alert> {
    const response = await apiClient.post<Alert>(`${this.basePath}/${id}/acknowledge`, { notes });
    return response;
  }

  /**
   * Resolve an alert
   */
  async resolve(id: string, resolution: string, notes?: string): Promise<Alert> {
    const response = await apiClient.post<Alert>(`${this.basePath}/${id}/resolve`, {
      resolution,
      notes,
    });
    return response;
  }

  /**
   * Snooze an alert
   */
  async snooze(id: string, durationMinutes: number): Promise<Alert> {
    const response = await apiClient.post<Alert>(`${this.basePath}/${id}/snooze`, {
      duration: durationMinutes,
    });
    return response;
  }

  /**
   * Get alert history
   */
  async getHistory(id: string): Promise<AlertHistoryEntry[]> {
    const response = await apiClient.get<AlertHistoryEntry[]>(`${this.basePath}/${id}/history`);
    return response;
  }

  /**
   * Bulk acknowledge multiple alerts
   */
  async bulkAcknowledge(
    alertIds: string[],
    notes?: string
  ): Promise<{ successful: number; failed: number; total: number }> {
    const response = await apiClient.post<{ successful: number; failed: number; total: number }>(
      `${this.basePath}/bulk/acknowledge`,
      { alertIds, notes }
    );
    return response;
  }

  /**
   * Bulk resolve multiple alerts
   */
  async bulkResolve(
    alertIds: string[],
    resolution: string,
    notes?: string
  ): Promise<{ successful: number; failed: number; total: number }> {
    const response = await apiClient.post<{ successful: number; failed: number; total: number }>(
      `${this.basePath}/bulk/resolve`,
      { alertIds, resolution, notes }
    );
    return response;
  }

  /**
   * Test notification channels
   */
  async testNotifications(): Promise<{ channels: NotificationTestResult; message: string }> {
    const response = await apiClient.post<{ channels: NotificationTestResult; message: string }>(
      `${this.basePath}/test-notifications`
    );
    return response;
  }

  /**
   * Get notification configuration
   */
  async getNotificationConfig(): Promise<{
    config: NotificationConfig;
    availableChannels: string[];
  }> {
    const response = await apiClient.get<{
      config: NotificationConfig;
      availableChannels: string[];
    }>(`${this.basePath}/notification-config`);
    return response;
  }
}

export const alertsApi = new AlertsApi();
